import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export type ChatMessage = {
  id: string
  conversation_id: string
  sender_id: string
  body: string
  created_at: string
  is_mine: boolean
}

export type ConversationWithMeta = {
  id: string
  other_user_id: string
  other_user_username: string
  other_user_first_name: string
  other_user_last_name: string
  is_blocked_by_me: boolean
  messages: ChatMessage[]
}

export function useConversation(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['conversation', conversationId],
    enabled: !!conversationId,
    refetchInterval: 8000, // poll every 8 seconds while open
    queryFn: async (): Promise<ConversationWithMeta | null> => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user || !conversationId) return null

      // Get the conversation
      const { data: conv } = await supabase
        .from('conversations')
        .select('id, user_a, user_b')
        .eq('id', conversationId)
        .maybeSingle()

      if (!conv) return null

      const otherUserId = conv.user_a === user.id ? conv.user_b : conv.user_a

      // Get other user info via RPC (bypasses RLS for minimal public fields)
      const { data: profileData } = await supabase.rpc(
        'get_resident_public_profile',
        { _user_id: otherUserId }
      )
      const resident =
        profileData && !(profileData as any)?.error
          ? (profileData as { id: string; username: string; first_name: string; last_name: string })
          : null

      // Check if I've blocked them
      const { data: blockRow } = await supabase
        .from('chat_blocks')
        .select('blocker_id')
        .eq('blocker_id', user.id)
        .eq('blocked_id', otherUserId)
        .maybeSingle()

      // Get messages
      const { data: msgs, error: msgsErr } = await supabase
        .from('chat_messages')
        .select('id, conversation_id, sender_id, body, created_at')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true })

      if (msgsErr) throw msgsErr

      return {
        id: conv.id,
        other_user_id: otherUserId,
        other_user_username: resident?.username ?? 'unknown',
        other_user_first_name: resident?.first_name ?? '',
        other_user_last_name: resident?.last_name ?? '',
        is_blocked_by_me: !!blockRow,
        messages: (msgs ?? []).map((m) => ({
          ...m,
          is_mine: m.sender_id === user.id,
        })),
      }
    },
  })
}
